import { useEffect, useRef, useState } from "preact/hooks";
import type { AccHub } from "../services/dataManagement";
import { isAccHubSelectable } from "../services/dataManagement/defaultAccHub";
import { AccHubDmStatusIcon } from "./AccHubDmStatusIcon";

const selectStyle: Record<string, string | number> = {
  width: "100%",
  font: "inherit",
  fontSize: 12,
  padding: "5px 8px",
  borderRadius: 4,
  border: "1px solid var(--vera-border)",
  background: "var(--vera-panel-bg)",
  color: "var(--vera-text)",
};

export function AccHubSelect({
  hubs,
  value,
  onChange,
  disabled,
}: {
  hubs: AccHub[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = hubs.find(h => h.id === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <label
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        fontSize: 10,
        color: "var(--vera-text-dim)",
        fontWeight: 600,
      }}
    >
      Hub
      <div ref={rootRef} style={{ position: "relative" }}>
        <button
          type="button"
          disabled={disabled || hubs.length === 0}
          onClick={() => setOpen(v => !v)}
          style={{
            ...selectStyle,
            display: "flex",
            alignItems: "center",
            gap: 8,
            textAlign: "left",
            cursor: disabled ? "not-allowed" : "pointer",
          }}
        >
          {selected ? (
            <>
              <AccHubDmStatusIcon dmProvisioned={selected.dmProvisioned} />
              <span
                style={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selected.name}
              </span>
            </>
          ) : (
            <span style={{ color: "var(--vera-text-dim)" }}>Select hub…</span>
          )}
          <span style={{ marginLeft: "auto", fontSize: 10 }}>
            {open ? "▴" : "▾"}
          </span>
        </button>
        {open && hubs.length > 0 && (
          <ul
            style={{
              position: "absolute",
              zIndex: 60,
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              margin: 0,
              padding: 4,
              listStyle: "none",
              maxHeight: 280,
              overflow: "auto",
              background: "var(--vera-panel-bg)",
              border: "1px solid var(--vera-border)",
              borderRadius: 4,
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            }}
          >
            {hubs.map(h => {
              const selectable = isAccHubSelectable(h);
              return (
                <li key={h.id}>
                  <button
                    type="button"
                    disabled={!selectable}
                    title={selectable ? undefined : "Custom integration needed"}
                    onClick={() => {
                      if (!selectable) return;
                      onChange(h.id);
                      setOpen(false);
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 8px",
                      font: "inherit",
                      fontSize: 12,
                      textAlign: "left",
                      border: "none",
                      borderRadius: 4,
                      cursor: selectable ? "pointer" : "not-allowed",
                      opacity: selectable ? 1 : 0.45,
                      background:
                        h.id === value
                          ? "rgba(127,179,213,0.15)"
                          : "transparent",
                      color: selectable
                        ? "var(--vera-text)"
                        : "var(--vera-text-dim)",
                    }}
                  >
                    <AccHubDmStatusIcon dmProvisioned={h.dmProvisioned} />
                    <span style={{ flex: 1 }}>{h.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </label>
  );
}
